import { Option } from '@prenuvo/shared-library';
import moment from 'moment';
import { createContext, useState } from 'react';

import { useAuth, useUserIdsQuery } from '../hooks';
import { DateRangeType, StudyFilterInput } from '../types';

export type FilterContextType = {
  status: string;
  setStatus: (status: string) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  selectedNP: Option;
  setSelectedNP: (selectedNP: Option) => void;
  location: Option;
  setLocation: (location: Option) => void;
  dateRange: DateRangeType;
  setDateRange: (daterange: DateRangeType) => void;
  getFormattedFilters: () => Promise<StudyFilterInput>;
};

export const FilterContext = createContext<FilterContextType | undefined>(undefined);

const FilterProvider = ({ children }: { children: React.ReactNode }) => {
  const { allowedPrescriberLocations, view, user } = useAuth();
  const { fetchUsers } = useUserIdsQuery();
  const [status, setStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedNP, setSelectedNP] = useState<Option>({ label: 'All', value: 'all' });
  const [location, setLocation] = useState<Option>({ label: 'All', value: 'all' });
  const [dateRange, setDateRange] = useState<DateRangeType>({
    startDate: moment().subtract(1, 'months'),
    endDate: moment().add(1, 'months'),
  });

  const getFormattedFilters = async () => {
    const formattedFilter: StudyFilterInput = {
      status: status,
      location: location.value,
      dateStart: `${dateRange.startDate?.startOf('day')?.unix()}` || '0',
      dateEnd: `${dateRange.endDate?.endOf('day')?.unix()}` || '0',
      users: '',
      selectedNP: '',
    };

    if (view === 'np' && selectedNP.value === 'all') {
      formattedFilter.selectedNP = user?.userId;
    }

    if (selectedNP.value && selectedNP.value !== 'all') {
      formattedFilter.selectedNP = selectedNP.value;
    }

    if (status.includes('prescription-') && view === 'np' && location.value === 'all') {
      if (allowedPrescriberLocations.length === 0) {
        formattedFilter.skipFilter = true;
        return formattedFilter;
      }
      formattedFilter.location = allowedPrescriberLocations.map(({ code }) => code).join(',');
    }

    if (searchTerm) {
      const { data } = await fetchUsers({
        context: {
          clientName: 'pii',
        },
        variables: {
          userFilter: {
            firstname: searchTerm,
            lastname: searchTerm,
            fullname: searchTerm,
          },
        },
      });
      formattedFilter.users = data.searchUsers.map((user: any) => user.userId).join(',');
    }

    return formattedFilter;
  };

  return (
    <FilterContext.Provider
      value={{
        status,
        setStatus,
        searchTerm,
        setSearchTerm,
        selectedNP,
        setSelectedNP,
        location,
        setLocation,
        dateRange,
        setDateRange,
        getFormattedFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
